import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { usePageConfiguration } from "./PageConfigurationContext";
import { ConfigurationContext } from "@contexts/Configuration";

const PageNavigationContext = createContext();

export const PAGE = {
  CHANNEL_SELECTION: 0,
  CHANNEL_HTML_CARD: 1,
  CHANNEL_SEARCH: 2,
};

export const NAVIGATION_DIRECTION = {
  NONE: "none",
  FORWARD: "forward",
  BACKWARD: "backward",
};

export const PageNavigationProvider = ({ children }) => {
  const landingPage = { pageType: PAGE.CHANNEL_SELECTION };
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const { selectedChannel, selectedBenefitTypeId } = usePageConfiguration();
  const [currentPage, setCurrentPage] = useState(landingPage);
  const [previousPage, setPreviousPage] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([landingPage]);
  const [navigationDirection, setNavigationDirection] = useState(NAVIGATION_DIRECTION.NONE);

  useEffect(() => {
    if (selectedChannel) {
      navigateForward();
    }
  }, [selectedChannel]);

  const getNextPageType = useCallback(
    (page) => {
      if (!appliedConfiguration || !selectedChannel || !selectedBenefitTypeId || !page) return 0;

      const nextPageType =
        appliedConfiguration?.customChannelPages
          .filter(
            (p) =>
              p.projectChannelId === selectedChannel &&
              p.benefitTypeId === selectedBenefitTypeId &&
              p.pageTypeId > page.pageType
          )
          .sort((a, b) => a.pageTypeId - b.pageTypeId)[0]?.pageTypeId || PAGE.CHANNEL_SEARCH;

      return nextPageType;
    },
    [appliedConfiguration, selectedChannel, selectedBenefitTypeId]
  );

  const navigateTo = useCallback(
    (page) => {
      const nextPage = page || landingPage;
      setCurrentPage(nextPage);
      setNavigationHistory((prev) => [...prev, nextPage]);
    },
    [landingPage]
  );

  const navigateBackward = useCallback(() => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const targetPage = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setPreviousPage(currentPage);
      setNavigationDirection(NAVIGATION_DIRECTION.BACKWARD);

      if (targetPage.pageType === PAGE.CHANNEL_SELECTION) {
        setNavigationHistory([landingPage]);
        setCurrentPage(landingPage);
      } else {
        setCurrentPage(targetPage);
      }
    }
  }, [navigationHistory, currentPage, landingPage]);

  const navigateForward = useCallback(() => {
    const nextPageType = getNextPageType(currentPage);
    const nextPage = { pageType: nextPageType };
    setNavigationHistory((prev) => [...prev, nextPage]);
    setPreviousPage(currentPage);
    setCurrentPage(nextPage);
    setNavigationDirection(NAVIGATION_DIRECTION.FORWARD);
  }, [getNextPageType, currentPage]);

  const startOver = useCallback(() => {
    setPreviousPage(currentPage);
    setCurrentPage(landingPage);
    setNavigationHistory([landingPage]);
    setNavigationDirection(NAVIGATION_DIRECTION.BACKWARD);
  }, [landingPage, currentPage]);

  const value = {
    currentPage,
    previousPage,
    navigationDirection,
    setNavigationDirection,
    navigateBackward,
    navigateForward,
    startOver,
  };

  return <PageNavigationContext.Provider value={value}>{children}</PageNavigationContext.Provider>;
};

export const usePageNavigation = () => {
  const context = useContext(PageNavigationContext);
  if (!context) {
    throw new Error("usePageNavigation must be used within a PageNavigationProvider");
  }
  return context;
};

export default PageNavigationContext;
